import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, CheckCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { FadeUp, WhatsAppIcon } from "./Reveal";
import { useLang } from "../i18n/LanguageContext";
import { SITE, waLink } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  checkin: "",
  checkout: "",
  guests: "",
  message: "",
  website: "",
};

export default function ContactSection() {
  const { T } = useLang();
  const L = T.contact;
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: null });
  };

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = L.errors.name;
    if (!EMAIL_RE.test(form.email)) errs.email = L.errors.email;
    if (form.message.trim().length < 5) errs.message = L.errors.message;
    if (form.checkin && form.checkout && form.checkout < form.checkin)
      errs.checkout = L.errors.checkout;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Error");
      }
      setSent(true);
      toast.success(L.toastOk);
    } catch (err) {
      toast.error(`${L.toastErr}`, { duration: 6000 });
    } finally {
      setSending(false);
    }
  };

  const field = (key, label, type = "text", required = false, extra = {}) => (
    <div className={extra.full ? "sm:col-span-2" : ""}>
      <label htmlFor={`contact-${key}`} className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-federasol-muted">
        {label} {required && <span className="text-federasol-rose">*</span>}
        {!required && <span className="normal-case text-federasol-muted/70"> (opcional)</span>}
      </label>
      <input
        id={`contact-${key}`}
        data-testid={`contact-input-${key}`}
        type={type}
        value={form[key]}
        onChange={set(key)}
        placeholder={extra.placeholder || ""}
        className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-federasol-cocoa outline-none transition-colors placeholder:text-federasol-muted/50 focus:border-federasol-terra ${
          errors[key] ? "border-red-400" : "border-federasol-terra/20"
        }`}
      />
      {errors[key] && <p className="mt-1.5 text-xs text-red-500">{errors[key]}</p>}
    </div>
  );

  return (
    <section id="contacto" data-testid="contact-section" className="py-14 md:py-24">
      <div className="container-x grid items-start gap-12 lg:grid-cols-[1fr_1.2fr]">
        <FadeUp>
          <p className="eyebrow">{L.eyebrow}</p>
          <h2 className="mt-4 font-serif text-3xl font-medium leading-tight text-federasol-cocoa sm:text-4xl lg:text-5xl">
            {L.title}
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-federasol-muted">
            {L.text}
          </p>

          <a
            href={waLink(T.wa.default)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-section-whatsapp-button"
            className="btn-whatsapp mt-8"
          >
            <WhatsAppIcon className="h-4 w-4" />
            {L.waBtn}
          </a>

          <div className="mt-10 space-y-2 text-sm text-federasol-muted">
            <p>
              <span className="font-semibold text-federasol-cocoa">{L.labels.email}:</span> {SITE.email}
            </p>
            <p>
              <span className="font-semibold text-federasol-cocoa">{L.labels.phone}:</span> {SITE.phoneDisplay}
            </p>
            <p>
              <span className="font-semibold text-federasol-cocoa">{L.labels.guests}:</span> {SITE.whatsappDisplay}
            </p>
          </div>
        </FadeUp>

        <FadeUp delay={0.12}>
          <div
            data-testid="contact-form-card"
            className="rounded-3xl border border-federasol-terra/10 bg-white p-5 shadow-[0_20px_60px_-24px_rgba(64,49,47,0.2)] sm:p-10"
          >
            <AnimatePresence mode="wait">
              {sent ? (
                <motion.div
                  key="success"
                  data-testid="contact-success"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex min-h-[360px] flex-col items-center justify-center text-center"
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-federasol-blush text-federasol-terra">
                    <CheckCheck className="h-7 w-7" />
                  </span>
                  <h3 className="mt-6 font-serif text-2xl font-semibold text-federasol-cocoa">
                    {L.successTitle(form.name.split(" ")[0])}
                  </h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-federasol-muted">
                    {L.successText(form.email)}
                  </p>
                  <button
                    data-testid="contact-send-another-button"
                    onClick={() => {
                      setForm(initialForm);
                      setSent(false);
                    }}
                    className="btn-outline mt-8"
                  >
                    {L.successBtn}
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="contact-form"
                  onSubmit={submit}
                  noValidate
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="grid gap-4 sm:grid-cols-2 sm:gap-5"
                >
                  {field("name", L.labels.name, "text", true, { full: true, placeholder: L.ph.name })}
                  {field("email", L.labels.email, "email", true, { full: true, placeholder: L.ph.email })}
                  {field("phone", L.labels.phone)}
                  <div>
                    <label htmlFor="contact-guests" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-federasol-muted">
                      {L.labels.guests} <span className="normal-case text-federasol-muted/70">{L.optional}</span>
                    </label>
                    <select
                      id="contact-guests"
                      data-testid="contact-input-guests"
                      value={form.guests}
                      onChange={set("guests")}
                      className="w-full rounded-xl border border-federasol-terra/20 bg-white px-4 py-3 text-sm text-federasol-cocoa outline-none transition-colors focus:border-federasol-terra"
                    >
                      <option value="">{L.choose}</option>
                      {["1", "2", "3", "4", "5", "6"].map((n) => (
                        <option key={n} value={n}>
                          {L.guestsOption(Number(n))}
                        </option>
                      ))}
                    </select>
                  </div>
                  {field("checkin", L.labels.checkin, "date")}
                  {field("checkout", L.labels.checkout, "date")}
                  <div className="sm:col-span-2">
                    <label htmlFor="contact-message" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-federasol-muted">
                      {L.labels.message} <span className="text-federasol-rose">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      data-testid="contact-input-message"
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      placeholder={L.ph.message}
                      className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-federasol-cocoa outline-none transition-colors placeholder:text-federasol-muted/50 focus:border-federasol-terra ${
                        errors.message ? "border-red-400" : "border-federasol-terra/20"
                      }`}
                    />
                    {errors.message && <p className="mt-1.5 text-xs text-red-500">{errors.message}</p>}
                  </div>

                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={set("website")}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />

                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      data-testid="contact-submit-button"
                      disabled={sending}
                      className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {sending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {L.sending}
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          {L.submit}
                        </>
                      )}
                    </button>
                    <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-federasol-muted">
                      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      {L.privacy}
                    </p>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
